import axios, { isAxiosError, type InternalAxiosRequestConfig } from 'axios'

import { toApiError } from './api-error'
import { AUTH_UNAUTHORIZED_EVENT, authEvents, tokenStorage } from './auth'
import { refreshSession } from './token-refresh'
import { env } from '@/shared/config/env'

/**
 * Set by the 401 path right before the unauthorized event, read (and
 * cleared) by the login page to show the "session expired" notice. A
 * deliberate sign-out never sets it.
 */
export const SESSION_EXPIRED_FLAG = 'app.auth.sessionExpired'

export const apiClient = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: env.apiTimeoutMs,
  headers: {
    'Content-Type': 'application/json',
  },
})

apiClient.interceptors.request.use((config) => {
  const token = tokenStorage.getAccessToken()
  if (token) {
    // The Eleva API expects the raw token - no "Bearer " prefix (the auth
    // middleware uses the exact header value as the Redis lookup key).
    config.headers.Authorization = token
  }
  // The backend localizes error bodies from the first tag it receives.
  const language = localStorage.getItem('app.language') ?? navigator.language
  if (language) config.headers['Accept-Language'] = language
  return config
})

/** Auth endpoints where a 401 is a real answer, not an expired session. */
const NO_REFRESH_PATHS = ['/auth/login', '/auth/refresh', '/auth/logout']

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean
}

function dropSession() {
  sessionStorage.setItem(SESSION_EXPIRED_FLAG, '1')
  tokenStorage.clear()
  authEvents.dispatchEvent(new Event(AUTH_UNAUTHORIZED_EVENT))
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    const apiError = toApiError(error)
    const config = isAxiosError(error) ? (error.config as RetriableConfig | undefined) : undefined

    if (
      apiError.status === 401 &&
      config &&
      !NO_REFRESH_PATHS.some((path) => config.url?.includes(path))
    ) {
      // Expired access token: refresh once (single-flight) and replay the
      // original request with the new token.
      if (!config._retried && tokenStorage.getRefreshToken()) {
        config._retried = true
        const refresh = await refreshSession()
        if (refresh.outcome === 'success') {
          config.headers.Authorization = refresh.accessToken
          return apiClient.request(config)
        }
        if (refresh.outcome === 'unavailable') {
          // Network hiccup or server trouble - the session may still be
          // fine. Fail this request only; never log the user out for it.
          return Promise.reject(apiError)
        }
        // rejected: the refresh token is definitively dead - fall through.
      }

      // No refresh token, replayed request still 401, or refresh rejected:
      // the session is gone. Clear it and tell the app.
      dropSession()
    }

    if (import.meta.env.DEV && apiError.code !== 'CANCELLED') {
      console.error(`[api] ${apiError.code}`, apiError)
    }

    // Everything downstream (React Query, UI) receives a normalized ApiError.
    return Promise.reject(apiError)
  },
)
